/**
 * Register — email + password (+ nombre opcional) contra POST /auth/register.
 *
 * Tras éxito, AuthContext queda autenticado y navegamos a "/".
 * En sprint 2 completo, navegaremos a "/onboarding/0" en lugar de "/".
 *
 * También expone el botón "Continuar con Google" — el backend resuelve
 * automáticamente si el email ya existe (vincula) o no (crea), por lo que
 * el botón en /register y /login es funcionalmente idéntico.
 *
 * Sprint 8 S8.4 (UX-008) — modo "oauth_pending":
 * Cuando un user nuevo intenta autenticarse vía Google sin invite_code
 * desde /login, el backend devuelve 400 + oauth_pending=True con el
 * email/name del Google profile. /login redirige aquí con esos datos en
 * `location.state` y el id_token. Render alternativo:
 *   - email read-only prefilled.
 *   - password + full_name + botones sociales ocultos (ya autorizamos).
 *   - submit invoca googleSignIn(oauthIdToken, inviteCode) sin re-OAuth.
 *   - F5 que pierde state → redirigir a /login con flash informativo.
 */
import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { AppleSignInButton } from "@/components/auth/AppleSignInButton";
import { ConsentCheckbox } from "@/components/auth/ConsentCheckbox";
import { GoogleSignInButton } from "@/components/auth/GoogleSignInButton";
import { AuthLayout } from "@/components/ui/AuthLayout";
import { Button } from "@/components/ui/Button";
import { FormError } from "@/components/ui/FormError";
import { Input } from "@/components/ui/Input";
import { useAuth } from "@/hooks/useAuth";
import { t, translateError } from "@/i18n/es";
import {
  MAX_INVITE_CODE_LEN,
  normalizeInviteCode,
  validateEmail,
  validateFullName,
  validateInviteCode,
  validatePassword,
} from "@/lib/validators";
import { ApiError } from "@/types/api";

interface OAuthPendingState {
  oauthIdToken: string;
  oauthEmail: string;
  oauthName?: string;
  oauthProvider: "google" | "apple";
  /** Sprint 14 S14.3: name object de Apple (solo viene en el 1er tap) que
   *  preservamos al encaminar a la pantalla de consentimiento, para que el
   *  reenvío de appleSignIn pueda popular full_name. */
  oauthAppleName?: { firstName: string; lastName: string };
}

/** Detecta si la URL trae el flag `?oauth_pending` (usado para detectar
 *  F5 que perdió el location.state — la URL persiste, el state no). */
function _hasOAuthPendingQuery(search: string): boolean {
  return new URLSearchParams(search).has("oauth_pending");
}

export function Register() {
  const { register, googleSignIn, appleSignIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Sprint 8 S8.4: extraer state OAuth-pending si venimos redirigidos
  // desde /login. Si no hay state pero la URL contiene ?oauth_pending
  // (caso típico: F5), tratamos como "state perdido" → redirect /login.
  const oauthState = (location.state ?? null) as OAuthPendingState | null;
  const isOAuthPending = Boolean(oauthState?.oauthIdToken);

  // Sprint 7 Bloque B1: invite_code va PRIMERO (antes del email) para
  // dejar claro que es requisito de la beta. El input auto-uppercase
  // mientras se escribe — cosmético + previene errores de copy/paste.
  const [inviteCode, setInviteCode] = useState("");
  const [email, setEmail] = useState(oauthState?.oauthEmail ?? "");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState(oauthState?.oauthName ?? "");
  const [inviteErr, setInviteErr] = useState<string | undefined>();
  const [emailErr, setEmailErr] = useState<string | undefined>();
  const [passErr, setPassErr] = useState<string | undefined>();
  const [nameErr, setNameErr] = useState<string | undefined>();
  const [formErr, setFormErr] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Sprint 14 S14.3 (R2 GDPR): consentimiento de Términos + Política.
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptErr, setAcceptErr] = useState<string | undefined>();

  // Sprint 8 S8.4: F5 con URL ?oauth_pending pero sin location.state →
  // el id_token se perdió, no podemos continuar. Redirigir a /login con
  // flash "Empieza de nuevo" en lugar de mostrar form roto.
  useEffect(() => {
    if (!isOAuthPending && _hasOAuthPendingQuery(location.search)) {
      navigate("/login", {
        replace: true,
        state: { flash: "oauthRestart" },
      });
    }
  }, [isOAuthPending, location.search, navigate]);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setFormErr(null);

    const inviteErrCode = validateInviteCode(inviteCode);

    // Sprint 8 S8.4 / S14.3: modo oauth_pending ("completar registro" social).
    // Google exige invite_code; Apple no. Ambos exigen consentimiento (S14.3).
    if (isOAuthPending && oauthState) {
      const isApple = oauthState.oauthProvider === "apple";

      // S14.3 (R2 GDPR): consentimiento obligatorio en ambos proveedores.
      if (!acceptTerms) {
        setAcceptErr(translateError("terms_not_accepted"));
        return;
      }
      setAcceptErr(undefined);

      // Google exige invite_code; Apple no (no está invite-gated).
      if (!isApple) {
        setInviteErr(
          inviteErrCode ? translateError(inviteErrCode.code) : undefined,
        );
        if (inviteErrCode) return;
      }

      setSubmitting(true);
      try {
        if (isApple) {
          await appleSignIn(
            oauthState.oauthIdToken,
            oauthState.oauthAppleName,
            true,
          );
        } else {
          await googleSignIn(
            oauthState.oauthIdToken,
            normalizeInviteCode(inviteCode),
            true,
          );
        }
        navigate("/", { replace: true });
      } catch (err) {
        if (err instanceof ApiError) {
          // Token social expirado/invalidado entre el primer OAuth y este
          // submit: no podemos completar — redirigir a /login con flash.
          if (
            err.code === "google_token_invalid"
            || err.code === "google_token_expired"
            || err.code === "apple_token_invalid"
          ) {
            navigate("/login", {
              replace: true,
              state: { flash: "oauthSessionExpired" },
            });
            return;
          }
          // Consentimiento no aceptado (defensa server-side): inline.
          if (err.code === "terms_not_accepted") {
            setAcceptErr(translateError(err.code));
            return;
          }
          // Invite inválido (solo Google): mantenemos al user en /register
          // con error inline. NO redirigir a /login.
          if (
            err.code === "invite_code_required"
            || err.code === "invite_code_invalid"
            || err.code === "invite_code_expired"
            || err.code === "invite_code_exhausted"
          ) {
            setInviteErr(translateError(err.code));
            return;
          }
          setFormErr(translateError(err.code));
        } else {
          setFormErr(translateError("network_error"));
        }
      } finally {
        setSubmitting(false);
      }
      return;
    }

    // Flow estándar (email + password + invite + name opcional + consentimiento).
    const emailErrCode = validateEmail(email);
    const passErrCode = validatePassword(password);
    const nameErrCode = validateFullName(fullName);
    setInviteErr(inviteErrCode ? translateError(inviteErrCode.code) : undefined);
    setEmailErr(emailErrCode ? translateError(emailErrCode.code) : undefined);
    setPassErr(passErrCode ? translateError(passErrCode.code) : undefined);
    setNameErr(nameErrCode ? translateError(nameErrCode.code) : undefined);
    // S14.3 (R2 GDPR): consentimiento obligatorio.
    setAcceptErr(acceptTerms ? undefined : translateError("terms_not_accepted"));
    if (
      inviteErrCode
      || emailErrCode
      || passErrCode
      || nameErrCode
      || !acceptTerms
    ) {
      return;
    }

    setSubmitting(true);
    try {
      await register(
        email.trim().toLowerCase(),
        password,
        normalizeInviteCode(inviteCode),
        fullName.trim() || undefined,
        true,
      );
      navigate("/", { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.field === "invite_code") setInviteErr(translateError(err.code));
        else if (err.field === "email") setEmailErr(translateError(err.code));
        else if (err.field === "password") setPassErr(translateError(err.code));
        else if (err.field === "full_name") setNameErr(translateError(err.code));
        // Algunos errores del invite vienen sin field (e.g. invite_code_expired
        // del lookup), pero el código sí los identifica.
        else if (
          err.code === "invite_code_required"
          || err.code === "invite_code_invalid"
          || err.code === "invite_code_expired"
          || err.code === "invite_code_exhausted"
        ) setInviteErr(translateError(err.code));
        else setFormErr(translateError(err.code));
      } else {
        setFormErr(translateError("network_error"));
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function onGoogleIdToken(idToken: string) {
    setFormErr(null);
    setInviteErr(undefined);
    setAcceptErr(undefined);
    // Sprint 7 Bloque B1: pasamos invite_code si el user lo rellenó.
    // El backend lo ignora en signin (email existente), y lo exige
    // en signup. Sprint 14 S14.3: pasamos también el consentimiento
    // (el backend lo ignora en signin; en signup nuevo, si falta, devuelve
    // terms_not_accepted y mostramos el error junto al checkbox).
    const inviteValue = normalizeInviteCode(inviteCode);
    setSubmitting(true);
    try {
      await googleSignIn(idToken, inviteValue || undefined, acceptTerms);
      navigate("/", { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.code === "terms_not_accepted") {
        setAcceptErr(translateError(err.code));
      } else if (err instanceof ApiError && (
        err.code === "invite_code_required"
        || err.code === "invite_code_invalid"
        || err.code === "invite_code_expired"
        || err.code === "invite_code_exhausted"
      )) {
        setInviteErr(translateError(err.code));
      } else {
        const code = err instanceof ApiError ? err.code : "network_error";
        setFormErr(translateError(code));
      }
    } finally {
      setSubmitting(false);
    }
  }

  function onGoogleError(code: string) {
    setFormErr(translateError(code));
  }

  async function onAppleIdToken(
    idToken: string,
    name?: { firstName: string; lastName: string },
  ) {
    setFormErr(null);
    setAcceptErr(undefined);
    setSubmitting(true);
    try {
      // S14.3: el consentimiento se manda en signup (backend lo ignora en
      // signin de user existente).
      await appleSignIn(idToken, name, acceptTerms);
      navigate("/", { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.code === "terms_not_accepted") {
        setAcceptErr(translateError(err.code));
      } else {
        const code = err instanceof ApiError ? err.code : "network_error";
        setFormErr(translateError(code));
      }
    } finally {
      setSubmitting(false);
    }
  }

  function onAppleError(code: string) {
    setFormErr(translateError(code));
  }

  // Sprint 8 S8.4 / S14.3: render simplificado para modo oauth_pending.
  if (isOAuthPending && oauthState) {
    const isApple = oauthState.oauthProvider === "apple";
    const subtitle = (
      isApple ? t.auth.oauthPendingSubtitleApple : t.auth.oauthPendingSubtitle
    ).replace("{email}", oauthState.oauthEmail);
    return (
      <AuthLayout
        title={t.auth.oauthPendingTitle}
        footer={
          <Link to="/login" className="hover:text-bone">
            {t.auth.toLogin}
          </Link>
        }
      >
        <div
          className="flex flex-col gap-6"
          data-testid="register-oauth-pending"
        >
          <p className="text-sm text-bone-muted">{subtitle}</p>

          <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
            <FormError message={formErr} />
            {/* Google está invite-gated; Apple no → ocultamos el invite. */}
            {!isApple && (
              <Input
                label={t.auth.inviteCodeLabel}
                type="text"
                autoComplete="off"
                autoCapitalize="characters"
                spellCheck={false}
                autoFocus
                required
                maxLength={MAX_INVITE_CODE_LEN}
                placeholder={t.auth.inviteCodePlaceholder}
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                error={inviteErr}
                data-testid="register-invite-input"
              />
            )}
            {/* Email read-only: viene del perfil social verificado,
                el user no puede cambiarlo sin re-OAuth. */}
            <Input
              label={t.auth.emailLabel}
              type="email"
              value={oauthState.oauthEmail}
              readOnly
              disabled
              onChange={() => {
                /* read-only */
              }}
              data-testid="register-oauth-email-readonly"
            />
            {/* S14.3 (R2 GDPR): consentimiento también en el signup social. */}
            <ConsentCheckbox
              checked={acceptTerms}
              onChange={setAcceptTerms}
              error={acceptErr}
            />
            <Button type="submit" loading={submitting} className="mt-2">
              {t.auth.oauthPendingSubmit}
            </Button>
          </form>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={t.auth.registerTitle}
      footer={
        <Link to="/login" className="hover:text-bone">
          {t.auth.toLogin}
        </Link>
      }
    >
      <div className="flex flex-col gap-6">
        {/* Apple PRIMERO (guideline Apple: igual o más prominente que otros sociales). */}
        <AppleSignInButton onIdToken={onAppleIdToken} onError={onAppleError} />
        <GoogleSignInButton onIdToken={onGoogleIdToken} onError={onGoogleError} />

        <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
          <FormError message={formErr} />
          <Input
            label={t.auth.inviteCodeLabel}
            type="text"
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            autoFocus
            required
            maxLength={MAX_INVITE_CODE_LEN}
            placeholder={t.auth.inviteCodePlaceholder}
            value={inviteCode}
            // Auto-uppercase mientras se escribe: cosmético, previene
            // errores de case y deja claro al user el formato esperado.
            onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
            error={inviteErr}
            data-testid="register-invite-input"
          />
          <Input
            label={t.auth.emailLabel}
            type="email"
            autoComplete="email"
            inputMode="email"
            required
            placeholder={t.auth.emailPlaceholder}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={emailErr}
          />
          <Input
            label={t.auth.passwordLabel}
            type="password"
            autoComplete="new-password"
            required
            placeholder={t.auth.passwordPlaceholder}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={passErr}
          />
          <Input
            label={t.auth.fullNameLabel}
            type="text"
            autoComplete="name"
            placeholder={t.auth.fullNamePlaceholder}
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            error={nameErr}
          />
          {/* S14.3 (R2 GDPR): consentimiento explícito obligatorio. */}
          <ConsentCheckbox
            checked={acceptTerms}
            onChange={setAcceptTerms}
            error={acceptErr}
          />
          <Button type="submit" loading={submitting} className="mt-2">
            {t.auth.registerCta}
          </Button>
        </form>
      </div>
    </AuthLayout>
  );
}
